/* eslint-disable parog-web/no-glint-expect-error-directive */
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  validateNumber,
  validatePresence
} from 'ember-changeset-validations/validators';
import type { IntlService } from 'ember-intl';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import type { ToastMessage } from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import CapillarityBloodGlucoseMeasuredJournalEvent from 'parog-web/models/capillarity-blood-glucose-measured-journal-event';
import type SessionService from 'parog-web/services/session';
import AlignedFieldsRadioGroup from 'ui-kit/components/aligned-fields-radio-group';
import NoContentMessage from 'ui-kit/components/no-content-message';
import NumberInput from 'ui-kit/components/number-input';
import ValidatedField from 'ui-kit/components/validated-field';

export default class CapillarityBloodGlucoseEditionFormComponent extends CrudJournalEventBaseComponent {
  static override forcDisplayName = 'Ma glycémie';
  static override fullComponentId = 'capillarity-blood-glucose/edition-form';
  static override journalEventClass =
    CapillarityBloodGlucoseMeasuredJournalEvent;

  @service declare intl: IntlService;
  @service declare session: SessionService;

  @tracked bloodGlucose: string = '';

  inputStyles = '!mt-[-12px]';
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;
  unitTextStyles = '!pt-[32px]';

  override get model(): typeof this.journalEventClass {
    // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
    const model = super.model;

    if (this.isNewMode) {
      model.occurredAt = new Date();
      model.measurementFasting = 'other';
      model.unit = 'g/L';
    }

    return model;
  }

  get shouldDisplayInsulinQuestions(): boolean {
    const insulin = this.changeset.get('insulin');
    return insulin == 'yes' || insulin == 'dontKnow';
  }

  override get toastMessage(): ToastMessage | null {
    return this.session.patient
      ? {
          message: 'Glycémie enregistrée. Merci !',
          title: 'Glycémie'
        }
      : super.toastMessage;
  }

  get unit(): string {
    return this.changeset.get('unit');
  }

  override get validations(): Record<string, unknown> {
    const validations = this.journalEventClass.validations(this.intl);

    validations['capillarityBloodGlucose'] = [
      validatePresence({
        presence: true,
        description: this.intl.t(
          'validations.capillarity-blood-glucose-measured-journal-event.capillarity-blood-glucose'
        )
      }),
      (key: string) => {
        let isValid;
        if (this.unit === 'mmol/L') {
          isValid = validateNumber({
            gt: 0,
            lt: 50,
            description: this.intl.t(
              'validations.capillarity-blood-glucose-measured-journal-event.capillarity-blood-glucose'
            )
          })(key, this.bloodGlucose);
        } else if (this.unit === 'g/L') {
          isValid = validateNumber({
            gt: 0,
            lt: 9,
            description: this.intl.t(
              'validations.capillarity-blood-glucose-measured-journal-event.capillarity-blood-glucose'
            )
          })(key, this.bloodGlucose);
        } else {
          return true;
        }

        if (isValid !== true) {
          if (typeof isValid === 'string') {
            return `${isValid} ${this.unit}`;
          }
        }

        return isValid;
      }
    ];

    return validations;
  }

  onBloodGlucoseChanged = (e: InputEvent): void => {
    const value = (e.target as HTMLInputElement).value;

    this.bloodGlucose = value;

    // Since in the JE, the blood glucose is stored in mmol, we need to convert it if the value was given by the user in g/L
    if (this.unit === 'g/L') {
      this.changeset.set(
        'capillarityBloodGlucose',
        value === '' ? '' : this.convertToMmol(value)
      );
    } else {
      this.changeset.set('capillarityBloodGlucose', value);
    }
  };

  onUnitChanged = async (newUnit: string): Promise<void> => {
    this.changeset.set('unit', newUnit);
    await this.changeset.validate('capillarityBloodGlucose');
  };

  override save = async () => {
    await super.save();
    this.analytics.trackPatientMeasurement('capillarity-blood-glucose');
  };

  private convertToMmol(value: string): string {
    return this.fixStringValue(
      CapillarityBloodGlucoseMeasuredJournalEvent.convertToMmol(
        parseFloat(value)
      )
    );
  }

  private fixStringValue(value: number): string {
    return value?.toFixed(2).replace(/\.?0+$/, '');
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <div class='field'>
          <Form.Date @labelName='Date' @propertyName='occurredAt' />
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Moment de la journée'
            @propertyName='momentOfTheDay'
            class='[.ui.form_&.fields_.field]:ml-[2em] md:[.ui.form_&.fields_.field]:ml-0'
            as |Group|
          >
            <Group.FieldRadio @value='morning' @label='Matin' />
            <Group.FieldRadio @value='noon' @label='Midi' />
            <Group.FieldRadio @value='afternoon' @label='Après-midi' />
            <Group.FieldRadio @value='evening' @label='Soir' />
            <Group.FieldRadio @value='night' @label='Nuit' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field required'>
          <Form.AlignedFieldsRadioGroup
            @title='Mesure effectuée'
            @propertyName='measurementFasting'
            class='[.ui.form_&.fields_.field]:ml-[2em] md:[.ui.form_&.fields_.field]:ml-0'
            as |Group|
          >
            <Group.FieldRadio @value='emptyStomach' @label='À jeun' />
            <Group.FieldRadio @value='beforeMeal' @label='Avant repas' />
            <Group.FieldRadio @value='afterMeal' @label='Après repas' />
            <Group.FieldRadio @value='other' @label='Autre' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <label>Glycémie capillaire</label>
        </div>
        <ValidatedField
          @propertyName='capillarityBloodGlucose'
          @changeset={{this.changeset}}
          @errorClass='left-aligned-validation-error-message'
        >
          <div class='field required'>
            <AlignedFieldsRadioGroup
              @current={{this.unit}}
              {{! @glint-expect-error we need to fix the typing on onChange and value }}
              @onChange={{this.onUnitChanged}}
              class='[.ui.form_&.fields]:gap-[2em] [.ui.form_&.fields_.field]:w-auto'
              as |Group|
            >
              <Group.FieldRadio
                @value='g/L'
                @label='g/L'
                data-test-unit='g/L'
              />
              <Group.FieldRadio
                @value='mmol/L'
                @label='mmol/L'
                data-test-unit='mmol/L'
              />
            </AlignedFieldsRadioGroup>
          </div>
          <div class='flex gap-2'>
            {{! <div class='two fields'> }}
            <div class='field w-[50%]'>
              {{! @glint-expect-error }}
              <NumberInput
                class={{this.inputStyles}}
                data-test-capillarity-blood-glucose
                @value={{this.bloodGlucose}}
                @onChange={{this.onBloodGlucoseChanged}}
                @step='0.01'
              />
            </div>
            <div class='field'>{{this.unit}}</div>
          </div>
        </ValidatedField>

        <div class='field'>
          <label>Insuline</label>
        </div>
        <div class='field'>
          <NoContentMessage>Dans le cadre de la prise en charge de mon diabète,
            je suis traité(e) par insuline
          </NoContentMessage>
          <Form.AlignedFieldsRadioGroup
            @propertyName='insulin'
            class='[.ui.form_&.fields_.field]:ml-[2em] md:[.ui.form_&.fields_.field]:ml-0'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='dontKnow' @label='Ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        {{#if this.shouldDisplayInsulinQuestions}}
          <div class='four fields'>
            <div class='field'>
              <Form.NumberInput
                @labelName='Insuline lente'
                @propertyName='slowInsulin'
              />
            </div>
            <div class='field {{this.unitTextStyles}}'>unités</div>
            <div class='field'>
              <Form.NumberInput
                @labelName='Insuline rapide'
                @propertyName='fastInsulin'
              />
            </div>
            <div class='field {{this.unitTextStyles}}'>unités</div>
          </div>
        {{/if}}

        <div class='field'>
          <Form.TextArea
            @labelName='Commentaire'
            @propertyName='comments'
            @rows={{4}}
          />
        </div>

      </:content>
    </Provider>
  </template>
}
